import { cn } from "@faire/prototype-playground/lib/utils";
import { StarFilled } from "@faire/slate-icons/StarFilled";
import React from "react";

import { PADX } from "../constants";

export interface ReviewSpotlightItem {
  quote: string;
  reviewerName: string;
  reviewerLocation?: string;
  stars: number;
  /** What they actually bought. Resolved from the review's product tokens by
   * the caller, so this component stays presentational. */
  productImageUrls: string[];
}

interface ReviewSpotlightProps {
  reviews: ReviewSpotlightItem[];
  title?: string;
  backgroundColor?: string;
}

/**
 * Review spotlight — the first of the review modules, and the simplest.
 *
 * Same review data Faire already has; this is only a different front end for
 * the brand.com surface (Ryan, 2026-08-17). Faire's own review row is built to
 * be *evaluated* — date, verified badge, brand reply, "read more" — because a
 * retailer comparing brands needs that. On the brand's own site none of it is
 * doing work, so this keeps the three things that carry the feeling: the words,
 * who said them, and what they bought.
 *
 * No dates, deliberately (Ryan). A date invites the reader to check whether the
 * review is recent, which is an evaluation question — exactly the mode this
 * surface isn't in.
 */
export const ReviewSpotlight: React.FC<ReviewSpotlightProps> = ({
  reviews,
  title,
  backgroundColor,
}) => {
  if (reviews.length === 0) return null;

  // One review gets the full width and the biggest type; several share the row.
  // Past three the quotes get too narrow to read as quotes.
  const shown = reviews.slice(0, 3);
  const isSolo = shown.length === 1;

  return (
    <div className="w-full" style={backgroundColor ? { backgroundColor } : undefined}>
      <div className={cn("py-14 md:py-20", PADX)}>
        {title ? (
          <h2 className="type-fs-section-heading text-fs-text-primary mb-10 text-center">
            {title}
          </h2>
        ) : null}
        <div
          className={cn(
            "flex flex-col gap-12",
            !isSolo && "md:flex-row md:gap-10"
          )}
        >
          {shown.map((review) => (
            <figure
              key={`${review.reviewerName}-${review.quote.slice(0, 24)}`}
              className={cn(
                "flex flex-1 flex-col items-center gap-5 text-center",
                isSolo && "mx-auto max-w-[900px]"
              )}
            >
              <div className="flex items-center gap-0.5" aria-label={`${review.stars} out of 5 stars`}>
                {Array.from({ length: review.stars }, (_, index) => (
                  <StarFilled key={index} width="16" height="16" />
                ))}
              </div>

              {/* The quote is the module. Sized so a short review still fills
                  the space rather than floating in it. */}
              <blockquote
                className={cn(
                  "text-fs-text-primary text-balance",
                  isSolo ? "type-fs-display-m" : "type-fs-display-s"
                )}
              >
                “{review.quote}”
              </blockquote>

              <figcaption className="type-fs-paragraph text-fs-text-subdued">
                {review.reviewerName}
                {review.reviewerLocation ? ` · ${review.reviewerLocation}` : ""}
              </figcaption>

              {review.productImageUrls.length > 0 ? (
                <div className="mt-1 flex items-center justify-center gap-3">
                  {review.productImageUrls.slice(0, 4).map((url) => (
                    <div
                      key={url}
                      className="bg-fs-surface-secondary h-16 w-16 overflow-hidden rounded"
                    >
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : null}
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
};
